// Google Analytics 4 utility with user-wise tracking for Clinician Web
// Measurement ID from Firebase config: G-YZB11MV97E

const GA_MEASUREMENT_ID =
    process.env.REACT_APP_GA_MEASUREMENT_ID ||
    process.env.REACT_APP_FIREBASE_MEASUREMENT_ID ||
    "G-YZB11MV97E";

declare global {
    interface Window {
        dataLayer: any[];
        gtag?: (...args: any[]) => void;
    }
}

// Initialize gtag
export const initGA = (): void => {
    if (typeof window === "undefined") return;
    if (window.gtag) return;

    const script1 = document.createElement("script");
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script1);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, {
        send_page_view: false,
        cookie_flags: "SameSite=None;Secure",
    });
};

// Set user properties for user-wise tracking
export const setAnalyticsUser = (userData: {
    id?: string | number;
    email?: string;
    fullName?: string;
    userType?: string;
}): void => {
    if (!window.gtag) return;

    const userId = String(userData.id || "");
    if (userId) {
        window.gtag("config", GA_MEASUREMENT_ID, {
            user_id: userId,
        });
        window.gtag("set", "user_properties", {
            user_type: "clinician",
            platform: "clinician_web",
            user_id_custom: userId,
            email_domain: userData.email
                ? userData.email.split("@")[1]
                : "unknown",
            display_name: userData.fullName || "anonymous",
            clinician_user_type: userData.userType || "CLINICIAN",
        });
    }
};

// Clear user on logout
export const clearAnalyticsUser = (): void => {
    if (!window.gtag) return;
    window.gtag("config", GA_MEASUREMENT_ID, {
        user_id: undefined,
    });
};

// Track page views
export const trackPageView = (url: string, title?: string): void => {
    if (!window.gtag) return;
    window.gtag("event", "page_view", {
        page_path: url,
        page_title: title || document.title,
        page_location: window.location.href,
        platform: "clinician_web",
    });
};

// Track custom events
export const trackEvent = (
    eventName: string,
    params: Record<string, any> = {}
): void => {
    if (!window.gtag) return;
    window.gtag("event", eventName, {
        ...params,
        platform: "clinician_web",
        timestamp: new Date().toISOString(),
    });
};

// Pre-defined event trackers for clinician actions
export const analyticsEvents = {
    // Auth
    login: () => trackEvent("login", { method: "email", platform: "clinician_web" }),
    logout: () => trackEvent("logout", { platform: "clinician_web" }),

    // Session management
    sessionViewed: (sessionId: string | number) =>
        trackEvent("session_viewed", { session_id: sessionId }),
    sessionStarted: (sessionId: string | number) =>
        trackEvent("session_started", { session_id: sessionId }),
    sessionCompleted: (sessionId: string | number) =>
        trackEvent("session_completed", { session_id: sessionId }),
    sessionCancelled: (sessionId: string | number, reason?: string) =>
        trackEvent("session_cancelled", { session_id: sessionId, reason }),

    // Patient management
    patientViewed: (patientId: string | number) =>
        trackEvent("patient_viewed", { patient_id: patientId }),
    patientListViewed: () => trackEvent("patient_list_viewed"),

    // Reports
    reportCreated: (sessionId: string | number) =>
        trackEvent("report_created", { session_id: sessionId }),
    reportViewed: (reportId: string | number) =>
        trackEvent("report_viewed", { report_id: reportId }),

    // Profile
    profileUpdated: (section: string) =>
        trackEvent("profile_updated", { section }),
    slotManagementUpdated: () => trackEvent("slot_management_updated"),
    sessionPackageUpdated: () => trackEvent("session_package_updated"),
    paymentDetailsUpdated: () => trackEvent("payment_details_updated"),

    // Payments
    paymentViewed: () => trackEvent("payments_viewed"),
    payoutViewed: () => trackEvent("payouts_viewed"),

    // Resources/Blog
    resourceViewed: (resourceId: string | number, type: string) =>
        trackEvent("resource_viewed", { resource_id: resourceId, resource_type: type }),
    blogRead: (slug: string) => trackEvent("blog_read", { blog_slug: slug }),

    // Dashboard
    dashboardViewed: () => trackEvent("dashboard_viewed"),
};
